import { Head } from '@inertiajs/react'
import { Mail, Shield, Trash2, Bug, CreditCard, ArrowRight } from 'lucide-react'

import { MarketingLayout } from '@/layouts/MarketingLayout'

interface SupportProps {
  appUrl: string
}

const SUPPORT_EMAIL = 'emiliano@tippy.com.mx'

const supportTopics = [
  {
    icon: Bug,
    title: 'Soporte de la app',
    description: 'Errores, problemas para entrar, fallas con notificaciones o cualquier detalle técnico.',
    cta: 'Reportar problema',
    href: `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Soporte técnico Tippy')}`,
  },
  {
    icon: CreditCard,
    title: 'Suscripción y pagos',
    description: 'Ayuda con tu plan, cobros, facturación o cambios de suscripción.',
    cta: 'Escribir a soporte',
    href: `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Ayuda con suscripción Tippy')}`,
  },
  {
    icon: Shield,
    title: 'Privacidad y datos',
    description: 'Consultas sobre tus datos, privacidad o uso de la información dentro de Tippy.',
    cta: 'Ver política de privacidad',
    href: '/privacy',
  },
  {
    icon: Trash2,
    title: 'Eliminar cuenta',
    description: 'Puedes eliminar tu cuenta directamente desde la app en Configuración > Cuenta > Eliminar cuenta.',
    cta: 'Ver política de privacidad',
    href: '/privacy',
  },
]

export default function Support({ appUrl }: SupportProps) {
  return (
    <MarketingLayout appUrl={appUrl}>
      <Head title="Soporte — Tippy" />

      <section className="pt-28 pb-20 lg:pt-36 lg:pb-24">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-secondary px-4 py-1.5 text-sm font-medium text-secondary-foreground mb-6">
              <Mail className="h-4 w-4" />
              Ayuda y soporte
            </div>

            <h1 className="text-4xl sm:text-5xl font-bold text-foreground tracking-tight leading-tight mb-6">
              ¿Necesitas ayuda con Tippy?
            </h1>

            <p className="text-lg text-muted-foreground mb-8 max-w-2xl">
              Si tienes dudas sobre tu cuenta, la app, tu suscripción o quieres solicitar
              eliminación de cuenta, escríbenos y te ayudamos.
            </p>

            <div className="rounded-2xl border border-border bg-card p-6 sm:p-8">
              <p className="text-sm font-medium uppercase tracking-wide text-muted-foreground mb-3">
                Contacto principal
              </p>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="inline-flex items-center gap-3 text-xl sm:text-2xl font-semibold text-foreground hover:text-primary transition-colors"
              >
                <Mail className="h-5 w-5" />
                {SUPPORT_EMAIL}
              </a>
              <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
                Cuéntanos qué pasó, desde qué correo o número usas Tippy y, si aplica,
                adjunta capturas para ayudarte más rápido.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {supportTopics.map((topic) => (
              <a
                key={topic.title}
                href={topic.href}
                className="group rounded-2xl border border-border bg-card p-6 hover:border-primary/50 hover:shadow-lg transition-all"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary mb-4">
                  <topic.icon className="h-6 w-6" />
                </div>

                <h2 className="text-xl font-semibold text-foreground mb-3">
                  {topic.title}
                </h2>
                <p className="text-muted-foreground mb-5">
                  {topic.description}
                </p>

                <span className="inline-flex items-center gap-2 text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                  {topic.cta}
                  <ArrowRight className="h-4 w-4" />
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl border border-border bg-muted/40 p-6 sm:p-8 lg:p-10">
            <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_0.7fr] gap-8 items-start">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
                  Eliminación de cuenta
                </h2>
                <p className="text-muted-foreground mb-5">
                  Puedes eliminar tu cuenta y los datos asociados directamente desde la app en
                  <strong> Configuración &gt; Cuenta &gt; Eliminar cuenta</strong>.
                </p>
                <ul className="space-y-3 text-sm text-foreground/90">
                  <li>• Inicia sesión en la app con la cuenta que quieres eliminar.</li>
                  <li>• Ve a Configuración &gt; Cuenta &gt; Eliminar cuenta.</li>
                  <li>• Confirma la acción para completar la eliminación.</li>
                  <li>• Si tu cuenta es la única propietaria de un negocio, la app te pedirá confirmar también la eliminación de ese negocio.</li>
                </ul>
                <p className="mt-5 text-sm text-muted-foreground">
                  Si necesitas ayuda adicional con una solicitud de privacidad o eliminación,
                  puedes escribirnos a <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline">{SUPPORT_EMAIL}</a>.
                </p>
              </div>

              <div className="rounded-2xl border border-border bg-card p-6">
                <p className="text-sm font-medium uppercase tracking-wide text-muted-foreground mb-3">
                  Más información
                </p>
                <a
                  href="/privacy"
                  className="inline-flex w-full items-center justify-center rounded-full bg-foreground px-5 py-3 text-sm font-semibold text-background hover:bg-foreground/90 transition-colors"
                >
                  Ver política de privacidad
                </a>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="mt-3 inline-flex w-full items-center justify-center rounded-full border border-foreground px-5 py-3 text-sm font-semibold text-foreground hover:bg-background transition-colors"
                >
                  Contactar soporte
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  )
}
